import { type MouseEvent, type ReactNode, createContext, useContext } from 'react';

import { Checkbox, Radio, type TypeTableColumn } from '@thefarmersfront/kitchen';

import type { CellContext, HeaderContext } from '@tanstack/react-table';

import type { TypeTableSelectionApi } from './useTableSelection';

import styles from './Table.module.scss';

/** 컬럼 최소 너비. 이보다 좁히면 헤더 리사이즈 핸들과 겹친다. */
const DEFAULT_COLUMN_MIN_SIZE = 49;
/** 너비를 정하지 않은 컬럼의 기본 너비. */
const DEFAULT_COLUMN_SIZE = 200;
/** 선택만 담는 컬럼(meta.selection 컬럼이 없을 때 앞에 붙인다)의 너비. */
const SELECTION_ONLY_COLUMN_SIZE = 48;
const SELECTION_ONLY_COLUMN_ID = 'selection';

export const TableSelectionContext = createContext<TypeTableSelectionApi<unknown> | null>(null);
/** 메서드 표기로 두어, 행 타입이 다른 표도 같은 context 로 넘길 수 있다. */
type TypeTableRowDim = { isRowDimmed(row: unknown): boolean };
export const TableRowDimContext = createContext<TypeTableRowDim | undefined>(undefined);

const isBlank = (value: unknown): boolean =>
  value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

/** 값이 비면 '-' 로 표시하는 기본 셀. 셀을 지정하지 않은 컬럼에 쓴다. */
const DefaultCell = <TData,>(info: CellContext<TData, unknown>): ReactNode => {
  const value = info.getValue();
  return isBlank(value) ? '-' : String(value);
};

const SelectionHeader = <TData,>({ children }: { children?: ReactNode; header: HeaderContext<TData, unknown> }) => {
  const selection = useContext(TableSelectionContext);
  const checkbox = selection?.mode === 'multiple' && (
    <Checkbox
      size="medium"
      checked={selection.headerState === 'all'}
      indeterminate={selection.headerState === 'some'}
      onChange={selection.toggleAll}
    />
  );

  return (
    <div className={styles['selection-content']}>
      {checkbox}
      {children}
    </div>
  );
};

const INTERACTIVE_SELECTOR = 'label, input, button, a';

/** 칸 어디를 눌러도 선택이 바뀐다. 체크박스(label) 자체를 누른 경우는 Checkbox 가 처리한다. 단일 선택은 Radio 로 그린다. */
const SelectionCell = <TData,>({ info, children }: { info: CellContext<TData, unknown>; children?: ReactNode }) => {
  const selection: TypeTableSelectionApi<TData> | null = useContext(TableSelectionContext);
  if (!selection) return <>{children}</>;

  const row = info.row.original;
  const index = info.row.index;
  const selectable = selection.isSelectable(row);
  const showCheckbox = selectable || selection.showDisabledCheckboxes;

  const isSelected = selection.isSelected(row, index);
  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target instanceof Element ? event.target : null;
    // 체크된 Radio 는 다시 눌러도 change 가 나지 않아, 단일 선택 해제는 클릭으로 받는다.
    if (target?.closest(`.${styles['selection-control']}`) && selection.mode === 'single' && isSelected) {
      selection.toggleRow(row, index);
      return;
    }
    if (target?.closest(INTERACTIVE_SELECTOR)) return;
    selection.toggleRow(row, index);
  };

  return (
    <div
      className={styles['selection-content']}
      data-row-selected={isSelected || undefined}
      data-row-dimmed={(selection.dimUnselectableRows && !selectable) || undefined}
      onClick={handleClick}
    >
      {showCheckbox &&
        (selection.mode === 'single' ? (
          <span className={styles['selection-control']}>
            <Radio
              size="medium"
              checked={isSelected}
              disabled={!selectable}
              onChange={() => selection.toggleRow(row, index)}
            />
          </span>
        ) : (
          <Checkbox
            size="medium"
            checked={isSelected}
            disabled={!selectable}
            onChange={() => selection.toggleRow(row, index)}
          />
        ))}
      {children}
    </div>
  );
};

const withDefaults = <TData,>(column: TypeTableColumn<TData>): TypeTableColumn<TData> => {
  if ('columns' in column && column.columns) {
    return { ...column, columns: column.columns.map(withDefaults) };
  }
  const cell = column.cell ?? DefaultCell;
  // 콘텐츠 너비 컬럼은 kitchen 이 너비를 재므로 기본 너비·최소 너비를 두지 않는다.
  if (column.meta?.columnFit) return { ...column, cell };
  return {
    ...column,
    size: column.size ?? (column.meta?.flex ? undefined : DEFAULT_COLUMN_SIZE),
    minSize: column.minSize ?? DEFAULT_COLUMN_MIN_SIZE,
    cell,
  };
};

/** isRowDimmed 가 참인 행을 흐리게 보이도록 행에 표시를 남긴다. 선택 칸의 흐림 표시와 같은 방식이다. */
const RowDimMarker = <TData,>({ row, children }: { row: TData; children?: ReactNode }) => {
  const rowDim = useContext(TableRowDimContext);
  return (
    <>
      {rowDim?.isRowDimmed(row) && <span data-row-dimmed hidden />}
      {children}
    </>
  );
};

const withRowDim = <TData,>(column: TypeTableColumn<TData>): TypeTableColumn<TData> => {
  if ('columns' in column && column.columns) {
    const [first, ...others] = column.columns;
    return first ? { ...column, columns: [withRowDim(first), ...others] } : column;
  }

  const { cell } = column;
  return Object.assign({}, column, {
    cell: (context: CellContext<TData, unknown>) => (
      <RowDimMarker row={context.row.original}>{typeof cell === 'function' ? cell(context) : cell}</RowDimMarker>
    ),
  });
};

const withSelection = <TData,>(column: TypeTableColumn<TData>): TypeTableColumn<TData> => {
  if (!column.meta?.selection || ('columns' in column && column.columns)) return column;

  const { header, cell } = column;
  // 스프레드는 컬럼 유니온 타입을 잃어 Object.assign 으로 원래 타입을 유지한다.
  return Object.assign({}, column, {
    header: (context: HeaderContext<TData, unknown>) => (
      <SelectionHeader header={context}>{typeof header === 'function' ? header(context) : header}</SelectionHeader>
    ),
    cell: (context: CellContext<TData, unknown>) => (
      <SelectionCell info={context}>{typeof cell === 'function' ? cell(context) : cell}</SelectionCell>
    ),
  });
};

// withSelection 이 최상위 컬럼만 감싸므로 같은 범위에서 찾는다.
const hasSelectionColumn = <TData,>(columns: TypeTableColumn<TData>[]): boolean =>
  columns.some((column) => column.meta?.selection && !('columns' in column && column.columns));

const countLeafColumns = <TData,>(columns: TypeTableColumn<TData>[]): number =>
  columns.reduce(
    (count, column) => count + ('columns' in column && column.columns ? countLeafColumns(column.columns) : 1),
    0
  );

/**
 * kitchen 헤더 툴팁은 화면 끝에서 자리를 바꾸지 않아, 오른쪽 컬럼 툴팁은 화면 밖으로 잘린다.
 * 위치를 정하지 않은 툴팁은 표의 오른쪽 절반 컬럼이면 아이콘에서 왼쪽으로 펼친다.
 */
const placeHeaderTooltips = <TData,>(
  columns: TypeTableColumn<TData>[],
  startLeafIndex: number,
  leafCount: number
): TypeTableColumn<TData>[] =>
  columns.map((column, index) => {
    const firstLeafIndex = startLeafIndex + countLeafColumns(columns.slice(0, index));
    const placed =
      'columns' in column && column.columns
        ? { ...column, columns: placeHeaderTooltips(column.columns, firstLeafIndex, leafCount) }
        : column;

    const tooltip = placed.meta?.headerTooltip;
    if (!tooltip || tooltip.placement) return placed;
    const placement = firstLeafIndex >= leafCount / 2 ? 'bottom-right' : 'bottom-left';
    return { ...placed, meta: { ...placed.meta, headerTooltip: { ...tooltip, placement } } };
  });

const withHeaderTooltipPlacement = <TData,>(columns: TypeTableColumn<TData>[]): TypeTableColumn<TData>[] =>
  placeHeaderTooltips(columns, 0, countLeafColumns(columns));

/**
 * 컬럼에 기존 표의 전역 기본값을 채우고 선택 칸을 그린다.
 * 선택 상태는 TableSelectionContext 로 읽어, 선택이 바뀌어도 컬럼(셀 함수)을 새로 만들지 않는다.
 * 셀 함수가 새로 만들어지면 입력 셀이 다시 마운트되어 입력 중인 값이 사라진다.
 */
export const enhanceColumns = <TData,>(
  columns: TypeTableColumn<TData>[],
  { hasSelection, hasRowDim }: { hasSelection: boolean; hasRowDim: boolean }
): TypeTableColumn<TData>[] => {
  const withDefaultsAndSelection = withHeaderTooltipPlacement(columns.map(withDefaults).map(withSelection));
  const [first, ...others] = withDefaultsAndSelection;
  const enhanced = hasRowDim && first ? [withRowDim(first), ...others] : withDefaultsAndSelection;
  if (!hasSelection || hasSelectionColumn(columns)) return enhanced;

  const selectionOnlyColumn: TypeTableColumn<TData> = {
    id: SELECTION_ONLY_COLUMN_ID,
    size: SELECTION_ONLY_COLUMN_SIZE,
    enableResizing: false,
    header: (context) => <SelectionHeader header={context} />,
    cell: (context) => <SelectionCell info={context} />,
  };
  return [selectionOnlyColumn, ...enhanced];
};
